import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { PromoFailReason } from "ipctg-inflow-types/InFlowEnums"
import { ParkPromoPOSTSuccessResponse } from "ipctg-inflow-types/InFlowParkAPI"

import api from "@/utilities/api"

import Cart from "@/globalComponents/organisms/Cart"
import { ParkPromoError } from "@/utilities/expectedErrors"
import { passesCheckoutQueryOptions } from "@/utilities/hooks/queries/passes"

interface FlexCartSearch {
  rateID: string
}

export const Route = createFileRoute(
  "/qr/$qrLookupID/passes/$epoch/purchase/cart"
)({
  staticData: {
    pageName: "Cart",
  },
  validateSearch: (search: Record<string, unknown>): FlexCartSearch => ({
    rateID: (search.rateID as string) || "",
  }),
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const { qrLookupID, epoch } = Route.useParams()
  const { rateID } = Route.useSearch()

  const {
    data: checkoutData,
    refetch,
    isRefetching,
  } = useSuspenseQuery(passesCheckoutQueryOptions({ rateID, epoch }))

  const applyPromotionMutation = useMutation({
    mutationFn: applyPromotionMutationFn,
    onSuccess: applyPromoOnSuccess,
  })

  const onCheckoutMutation = useMutation({
    mutationFn: onCheckoutMutationFn,
  })

  return (
    <Cart
      transaction={checkoutData.transactionDetails}
      productType="pass"
      mutations={{
        applyPromotion: applyPromotionMutation,
        resetPromotion: {
          mutate: () => refetch(),
          isPending: isRefetching,
        },
        onGoNext: onCheckoutMutation,
      }}
    />
  )

  async function applyPromotionMutationFn(promoCode: string) {
    if (!promoCode) {
      throw new ParkPromoError(PromoFailReason.InvalidCode)
    }

    const rsp = await api.park.promo.POST({
      promoCode,
    })

    if ("failReason" in rsp) {
      throw new ParkPromoError(rsp.failReason)
    }

    return rsp
  }

  function applyPromoOnSuccess(data: ParkPromoPOSTSuccessResponse) {
    queryClient.setQueryData(
      passesCheckoutQueryOptions({
        rateID,
        epoch,
      }).queryKey,
      data
    )
  }

  //we dont actually care if this throws or not
  async function onCheckoutMutationFn(promoCode: string) {
    if (promoCode) {
      try {
        await api.park.promo.POST({
          promoCode,
        })
      } catch (e) {
        console.error(e)
      }
    }
    await navigate({
      to: "/qr/$qrLookupID/passes/$epoch/purchase/checkout",
      params: { qrLookupID, epoch },
      search: { rateID },
    })
  }
}
